import { useMemo, useState } from 'react';
import useSWR from 'swr';
import { useBackendApi } from '@/hooks/fetch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ActivityChart, type ActivitySeries } from '../monitorShared/Charts';
import {
    AlertsTable, DataTable, MonitorHeader, SearchBox, StatCard, StatusBanner, StatusLabel,
    type HistoryRange, type LiveRange, type MonitorAlert, type MonitorStatus, type Severity, type ViewMode,
} from '../monitorShared/MonitorChrome';
import { fmtAgo, fmtBytes, fmtDateTime, fmtMs, fmtNum } from '../monitorShared/format';


// =============================================
//  Types de l'API /vanta/events/*
// =============================================
type Point = {
    t: number;
    eventsPs: number;
    fromClBps: number;
    toClBps: number;
    cpuMsPs: number;
    maxHandler: number;
    incomingPs: number;
    localOutPs: number;
    toClPs: number;
    alerts: number;
};
type ResourceRow = {
    name: string;
    status?: Severity;
    score?: number;
    cpuMsPs: number;
    cpuMax: number;
    netInPs: number;
    netInBps: number;
    outClPs: number;
    outClKiBps: number;
    locInPs: number;
    locOutPs: number;
    httpPm: number;
    httpErr?: number;
    async?: number;
};
type EventRow = {
    res: string;
    name: string;
    net: boolean;
    netInPs: number;
    netInBps: number;
    netMax: number;
    locInPs: number;
    callsPs: number;
    cpuMsPs: number;
    cpuAvg: number;
    cpuMax: number;
    async: number;
    outClPs: number;
    outClTargetsPs: number;
    outClKiBps: number;
    outClMax: number;
    latent: number;
    locOutPs: number;
    total: number;
};
type PlayerRow = {
    id: number;
    name: string;
    events: number;
    eventsPs: number;
    bytes: number;
    bps: number;
    status: Severity;
    top: { name: string; count: number; bytes: number; ps: number }[];
};
type HttpRow = {
    res: string;
    host: string;
    requests: number;
    perMin: number;
    errors: number;
    bytesOut: number;
    bytesIn: number;
    latAvg: number;
    latMax: number;
};
type SecurityRow = { severity: Severity; kind: string; subject: string; resource?: string; detail: string };
type EventNameRow = { res: string; name: string; net: boolean; handlers: number; seen: number };
type HookResource = { name: string; state: 'reporting' | 'pending' | 'not_installed' | 'off'; v?: string; clk?: string; ago?: number };
type DataResp = {
    ts: number;
    range: LiveRange;
    windowCount: number;
    status: MonitorStatus;
    cards: { eventsPs: number; fromClBps: number; toClKiBps: number; cpuMsPs: number; maxHandler: number; alerts: number; critical: number };
    series: Point[];
    resources: ResourceRow[];
    events: EventRow[];
    players: PlayerRow[];
    http: HttpRow[];
    security: SecurityRow[];
    alerts: MonitorAlert[];
    eventNames: EventNameRow[];
    hookResources: HookResource[];
    thresholds: Record<string, number>;
    history: { sizeBytes: number; retentionDays: number };
    error?: string;
};
type HistoryResp = {
    days: number;
    from: number | null;
    to: number | null;
    buckets: number;
    series: Point[];
    resources: ResourceRow[];
    error?: string;
};

const ACTIVITY_SERIES: ActivitySeries<Point>[] = [
    { key: 'fromClBps', label: 'From clients', fmt: (v) => `${fmtBytes(v)}/s` },
    { key: 'toClBps', label: 'To clients (wire)', fmt: (v) => `${fmtBytes(v)}/s` },
    { key: 'cpuMsPs', label: 'Handler CPU', fmt: (v) => `${fmtNum(v, 3)} ms/s` },
    { key: 'incomingPs', label: 'Incoming', fmt: (v) => `${fmtNum(v)} /s` },
    { key: 'localOutPs', label: 'Local out', fmt: (v) => `${fmtNum(v)} /s` },
    { key: 'toClPs', label: 'To clients /s', fmt: (v) => `${fmtNum(v)} /s` },
];

const mono = (s: string) => <span className="font-mono text-foreground">{s}</span>;
const rate = (v: number) => (v ? fmtNum(v) : <span className="text-muted-foreground/60">0</span>);


/**
 * Event Monitor : events réseau / locaux, handlers, bande passante, HTTP par ressource.
 */
export default function EventMonitorPage() {
    const [mode, setMode] = useState<ViewMode>('live');
    const [range, setRange] = useState<LiveRange>('5m');
    const [histRange, setHistRange] = useState<HistoryRange>('1');
    const [livePaused, setLivePaused] = useState(false);
    const [tab, setTab] = useState('resources');
    const [eventFilter, setEventFilter] = useState('');
    const [search, setSearch] = useState('');

    const dataApi = useBackendApi<DataResp>({ method: 'GET', path: '/vanta/events/data' });
    const historyApi = useBackendApi<HistoryResp>({ method: 'GET', path: '/vanta/events/history' });

    const live = useSWR(mode === 'live' ? `/vanta/events/data?range=${range}` : null, async () => {
        const data = await dataApi({ queryParams: { range } });
        if (!data) throw new Error('empty_response');
        if (data.error) throw new Error(data.error);
        if (!data.cards || !data.series) throw new Error('invalid_response');
        return data;
    }, {
        refreshInterval: livePaused ? 0 : 5000,
        revalidateOnFocus: false,
        keepPreviousData: true,
    });
    const hist = useSWR(mode === 'history' ? `/vanta/events/history?days=${histRange}` : null, async () => {
        const data = await historyApi({ queryParams: { days: histRange } });
        if (!data) throw new Error('empty_response');
        if (data.error) throw new Error(data.error);
        return data;
    }, { revalidateOnFocus: false, refreshInterval: 60_000 });

    const d = live.data;
    const h = hist.data;
    const series = mode === 'live' ? d?.series ?? [] : h?.series ?? [];

    const cards = useMemo(() => {
        if (mode === 'live' && d) return d.cards;
        if (mode === 'history' && h && h.series.length) {
            const n = h.series.length;
            const avg = (k: keyof Point) => h.series.reduce((a, p) => a + (p[k] as number), 0) / n;
            return {
                eventsPs: avg('eventsPs'),
                fromClBps: avg('fromClBps'),
                toClKiBps: avg('toClBps') / 1024,
                cpuMsPs: avg('cpuMsPs'),
                maxHandler: Math.max(...h.series.map((p) => p.maxHandler)),
                alerts: h.series.reduce((a, p) => a + p.alerts, 0),
                critical: 0,
            };
        }
        return null;
    }, [mode, d, h]);

    const spark = (k: keyof Point) => series.map((p) => p[k] as number);
    const T = d?.thresholds;

    const filteredEvents = useMemo(() => {
        if (!d) return [];
        const f = eventFilter.toLowerCase();
        return f ? d.events.filter((e) => e.res.toLowerCase().includes(f) || e.name.toLowerCase().includes(f)) : d.events;
    }, [d, eventFilter]);

    const searchResults = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!d || q.length < 2) return [];
        const out: { kind: string; label: string; detail: string; go: () => void }[] = [];
        for (const r of d.resources) if (r.name.toLowerCase().includes(q)) {
            out.push({ kind: 'Resource', label: r.name, detail: `${fmtNum(r.cpuMsPs, 3)} ms/s CPU · ${fmtNum(r.outClKiBps, 2)} KiB/s to clients`, go: () => { setEventFilter(r.name); setTab('events'); } });
        }
        for (const e of d.events) if (e.name.toLowerCase().includes(q)) {
            out.push({ kind: e.net ? 'Net event' : 'Event', label: e.name, detail: `${e.res} · ${fmtNum(e.callsPs)} calls/s · max ${fmtMs(e.cpuMax)}`, go: () => { setEventFilter(e.name); setTab('events'); } });
        }
        for (const n of d.eventNames) if (n.name.toLowerCase().includes(q) && !d.events.some((e) => e.res === n.res && e.name === n.name)) {
            out.push({ kind: 'Registered', label: n.name, detail: `${n.res} · ${n.handlers} handler(s)${n.net ? ' · net' : ''} · idle`, go: () => setTab('names') });
        }
        for (const p of d.players) if (p.name.toLowerCase().includes(q) || String(p.id) === q) {
            out.push({ kind: 'Player', label: `${p.name} (#${p.id})`, detail: `${fmtNum(p.eventsPs)} events/s · ${fmtBytes(p.bps)}/s`, go: () => setTab('players') });
        }
        for (const x of d.http) if (x.host.toLowerCase().includes(q)) {
            out.push({ kind: 'HTTP', label: x.host, detail: `${x.res} · ${x.requests} requests`, go: () => setTab('http') });
        }
        return out.slice(0, 200);
    }, [d, search]);

    return (
        <div className="w-full space-y-4">
            <MonitorHeader
                title="Event Monitor"
                updatedAt={d?.ts ?? null}
                mode={mode} setMode={setMode}
                range={range} setRange={setRange}
                histRange={histRange} setHistRange={setHistRange}
                livePaused={livePaused} setLivePaused={setLivePaused}
            />
            <StatusBanner
                feature="events"
                status={d?.status}
                historySize={d?.history?.sizeBytes}
                onChanged={() => live.mutate()}
            />
            {live.error && mode === 'live' && (
                <div className="rounded-lg border border-[#d03b3b] px-4 py-2 text-sm text-[#d03b3b]">
                    Failed to load data: {(live.error as Error).message}
                </div>
            )}

            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
                <StatCard label="Events /s" value={fmtNum(cards?.eventsPs ?? 0)} unit="/s"
                    sub={d && mode === 'live' ? `${d.status.playerCount} players online` : 'average'}
                    spark={spark('eventsPs')} />
                <StatCard label="From clients" value={fmtBytes(cards?.fromClBps ?? 0)} unit="/s"
                    sub="client → server payloads" spark={spark('fromClBps')} />
                <StatCard label="To clients" value={fmtNum(cards?.toClKiBps ?? 0, 2)} unit="KiB/s"
                    sub="wire, broadcasts × players" spark={spark('toClBps')}
                    tone={T && cards && cards.toClKiBps >= T.resOutCritKiBps ? 'critical' : T && cards && cards.toClKiBps >= T.resOutWarnKiBps ? 'warning' : undefined} />
                <StatCard label="Handler CPU" value={fmtNum(cards?.cpuMsPs ?? 0, 3)} unit="ms/s"
                    sub={<>max handler <span className={(cards?.maxHandler ?? 0) >= (T?.handlerWarnMs ?? 50) ? 'text-[#b47c00] dark:text-[#fab219]' : ''}>{fmtMs(cards?.maxHandler ?? 0)}</span></>}
                    spark={spark('cpuMsPs')} />
                <StatCard label="Alerts" value={String(cards?.alerts ?? 0)}
                    sub={mode === 'live' ? `${cards?.critical ?? 0} critical · last 60s` : 'raised in period'}
                    spark={spark('alerts')}
                    tone={cards?.critical ? 'critical' : cards?.alerts ? 'warning' : undefined} />
            </div>

            <div className="rounded-lg border bg-card p-4">
                <div className="mb-2 text-sm font-semibold">Activity</div>
                <ActivityChart data={series} series={ACTIVITY_SERIES} withDate={mode === 'history'} />
            </div>

            {mode === 'history' ? (
                <div className="space-y-2">
                    <div className="text-xs text-muted-foreground">
                        {h ? `${h.buckets} buckets · ${fmtDateTime(h.from)} → ${fmtDateTime(h.to)} · averages over the period` : 'Loading history…'}
                    </div>
                    <ResourcesTable rows={h?.resources ?? []} history />
                </div>
            ) : (
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="h-auto flex-wrap justify-start">
                        <TabsTrigger value="resources">Resources</TabsTrigger>
                        <TabsTrigger value="events">Events</TabsTrigger>
                        <TabsTrigger value="players">Players</TabsTrigger>
                        <TabsTrigger value="security">Security{d?.security.length ? ` (${d.security.length})` : ''}</TabsTrigger>
                        <TabsTrigger value="http">HTTP</TabsTrigger>
                        <TabsTrigger value="alerts">Alerts{d?.cards.alerts ? ` (${d.cards.alerts})` : ''}</TabsTrigger>
                        <TabsTrigger value="names">Event names</TabsTrigger>
                        <TabsTrigger value="coverage">Coverage</TabsTrigger>
                        <TabsTrigger value="search">Search</TabsTrigger>
                    </TabsList>

                    <TabsContent value="resources">
                        <ResourcesTable rows={d?.resources ?? []} onSelect={(name) => { setEventFilter(name); setTab('events'); }} />
                    </TabsContent>

                    <TabsContent value="events" className="space-y-2">
                        <SearchBox value={eventFilter} onChange={setEventFilter} placeholder="Filter by event or resource" />
                        <DataTable<EventRow>
                            rows={filteredEvents}
                            rowKey={(e) => `${e.res}|${e.name}`}
                            defaultSort="cpuMsPs"
                            columns={[
                                { key: 'name', label: 'Event', render: (e) => <span className="font-mono text-foreground">{e.name}{e.net && <span className="ml-1.5 text-[10px] text-muted-foreground">NET</span>}</span> },
                                { key: 'res', label: 'Resource', render: (e) => mono(e.res) },
                                { key: 'netInPs', label: 'From clients', align: 'right', render: (e) => rate(e.netInPs), title: 'events/s received from clients' },
                                { key: 'netInBps', label: 'In B/s', align: 'right', render: (e) => (e.netInBps ? fmtBytes(e.netInBps) : rate(0)) },
                                { key: 'netMax', label: 'Max in', align: 'right', render: (e) => (e.netMax ? fmtBytes(e.netMax) : rate(0)) },
                                { key: 'locInPs', label: 'Local in', align: 'right', render: (e) => rate(e.locInPs) },
                                { key: 'callsPs', label: 'Calls/s', align: 'right', render: (e) => rate(e.callsPs), title: 'handler invocations/s' },
                                { key: 'cpuMsPs', label: 'CPU ms/s', align: 'right', render: (e) => fmtNum(e.cpuMsPs, 3) },
                                { key: 'cpuAvg', label: 'Avg', align: 'right', render: (e) => fmtMs(e.cpuAvg) },
                                { key: 'cpuMax', label: 'Max', align: 'right', render: (e) => <span className={T && e.cpuMax >= T.handlerWarnMs ? 'text-[#b47c00] dark:text-[#fab219]' : ''}>{fmtMs(e.cpuMax)}</span> },
                                { key: 'outClPs', label: 'To clients', align: 'right', render: (e) => rate(e.outClPs) },
                                { key: 'outClTargetsPs', label: 'Recipients', align: 'right', render: (e) => rate(e.outClTargetsPs), title: 'recipients/s (-1 = all connected players)' },
                                { key: 'outClKiBps', label: 'KiB/s out', align: 'right', render: (e) => rate(e.outClKiBps) },
                                { key: 'locOutPs', label: 'Local out', align: 'right', render: (e) => rate(e.locOutPs) },
                                { key: 'async', label: 'Async', align: 'right', render: (e) => rate(e.async), title: 'handler calls that yielded (Wait/Await): time not counted' },
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="players">
                        <DataTable<PlayerRow>
                            rows={d?.players ?? []}
                            rowKey={(p) => String(p.id)}
                            defaultSort="eventsPs"
                            empty="No client event received in this range."
                            columns={[
                                { key: 'status', label: 'Status', value: (p) => (p.status === 'critical' ? 2 : p.status === 'warning' ? 1 : 0), render: (p) => <StatusLabel status={p.status} /> },
                                { key: 'id', label: 'ID', align: 'right' },
                                { key: 'name', label: 'Player', render: (p) => <span className="text-foreground">{p.name}</span> },
                                { key: 'eventsPs', label: 'Events/s', align: 'right', render: (p) => fmtNum(p.eventsPs) },
                                { key: 'events', label: 'Events', align: 'right', render: (p) => fmtNum(p.events) },
                                { key: 'bps', label: 'B/s', align: 'right', render: (p) => fmtBytes(p.bps) },
                                { key: 'bytes', label: 'Total', align: 'right', render: (p) => fmtBytes(p.bytes) },
                                {
                                    key: 'top', label: 'Top events', value: (p) => p.top[0]?.count ?? 0,
                                    render: (p) => <span className="font-mono text-muted-foreground">{p.top.slice(0, 3).map((t) => `${t.name} ×${t.count}`).join(' · ')}</span>,
                                },
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="security">
                        <DataTable<SecurityRow>
                            rows={d?.security ?? []}
                            rowKey={(s) => `${s.kind}|${s.subject}|${s.resource}`}
                            defaultSort="severity"
                            empty="Nothing suspicious in this range."
                            columns={[
                                { key: 'severity', label: 'Status', value: (s) => ({ critical: 3, warning: 2, info: 1, ok: 0 }[s.severity] ?? 0), render: (s) => <StatusLabel status={s.severity} /> },
                                { key: 'kind', label: 'Finding', render: (s) => <span className="text-muted-foreground">{s.kind.replace(/_/g, ' ')}</span> },
                                { key: 'subject', label: 'Subject', render: (s) => mono(s.subject) },
                                { key: 'resource', label: 'Resource', render: (s) => mono(s.resource ?? '–') },
                                { key: 'detail', label: 'Detail', render: (s) => <span className="text-foreground">{s.detail}</span> },
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="http">
                        <DataTable<HttpRow>
                            rows={d?.http ?? []}
                            rowKey={(x) => `${x.res}|${x.host}`}
                            defaultSort="requests"
                            empty="No outgoing PerformHttpRequest in this range."
                            columns={[
                                { key: 'res', label: 'Resource', render: (x) => mono(x.res) },
                                { key: 'host', label: 'Host', render: (x) => mono(x.host) },
                                { key: 'requests', label: 'Requests', align: 'right' },
                                { key: 'perMin', label: '/min', align: 'right', render: (x) => fmtNum(x.perMin) },
                                { key: 'errors', label: 'Errors', align: 'right', render: (x) => (x.errors ? <span className="text-[#d03b3b]">{x.errors}</span> : rate(0)) },
                                { key: 'bytesOut', label: 'Sent', align: 'right', render: (x) => fmtBytes(x.bytesOut) },
                                { key: 'bytesIn', label: 'Received', align: 'right', render: (x) => fmtBytes(x.bytesIn) },
                                { key: 'latAvg', label: 'Avg latency', align: 'right', render: (x) => `${x.latAvg} ms` },
                                { key: 'latMax', label: 'Max', align: 'right', render: (x) => `${x.latMax} ms` },
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="alerts">
                        <AlertsTable alerts={d?.alerts ?? []} />
                    </TabsContent>

                    <TabsContent value="names">
                        <DataTable<EventNameRow>
                            rows={d?.eventNames ?? []}
                            rowKey={(n) => `${n.res}|${n.name}`}
                            defaultSort="res"
                            defaultDesc={false}
                            empty="No registry received yet (sent by the hook every 60 s)."
                            columns={[
                                { key: 'res', label: 'Resource', render: (n) => mono(n.res) },
                                { key: 'name', label: 'Event', render: (n) => mono(n.name) },
                                { key: 'net', label: 'Type', value: (n) => (n.net ? 1 : 0), render: (n) => (n.net ? 'net (client-triggerable)' : 'local') },
                                { key: 'handlers', label: 'Handlers', align: 'right' },
                                { key: 'seen', label: 'Activity', align: 'right', render: (n) => (n.seen ? fmtNum(n.seen) : <span className="text-muted-foreground/60">idle</span>) },
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="coverage">
                        <DataTable<HookResource>
                            rows={d?.hookResources ?? []}
                            rowKey={(r) => r.name}
                            defaultSort="state"
                            defaultDesc={false}
                            empty="Waiting for the monitor resource report."
                            columns={[
                                { key: 'name', label: 'Lua resource', render: (r) => mono(r.name) },
                                {
                                    key: 'state', label: 'Hook', value: (r) => ({ reporting: 3, off: 2, pending: 1, not_installed: 0 }[r.state] ?? 0),
                                    render: (r) => ({
                                        reporting: <span className="text-foreground">reporting</span>,
                                        off: <span className="text-muted-foreground">collection off</span>,
                                        pending: <span className="text-[#b47c00] dark:text-[#fab219]">installed, restart pending</span>,
                                        not_installed: <span className="text-muted-foreground">not reporting</span>,
                                    }[r.state]),
                                },
                                { key: 'v', label: 'Version', render: (r) => r.v ?? '–' },
                                { key: 'clk', label: 'Clock', render: (r) => r.clk ?? '–' },
                                { key: 'ago', label: 'Last report', align: 'right', render: (r) => (r.ago !== undefined ? `${r.ago}s ago` : '–') },
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="search" className="space-y-2">
                        <SearchBox value={search} onChange={setSearch} placeholder="Search resources, events, players, hosts…" />
                        {search.trim().length >= 2 && (
                            <div className="divide-y rounded-lg border bg-card">
                                {searchResults.length === 0 && <div className="px-4 py-6 text-center text-sm text-muted-foreground">No match.</div>}
                                {searchResults.map((r, i) => (
                                    <button key={i} type="button" onClick={r.go}
                                        className="flex w-full items-baseline gap-3 px-4 py-2 text-left text-sm hover:bg-muted">
                                        <span className="w-24 shrink-0 text-xs uppercase tracking-wider text-muted-foreground">{r.kind}</span>
                                        <span className="font-mono">{r.label}</span>
                                        <span className="ml-auto truncate text-xs text-muted-foreground">{r.detail}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            )}
            {d && mode === 'live' && (
                <div className="text-xs text-muted-foreground">
                    {d.windowCount} reports in range · data refreshed every 5 s · last report {fmtAgo(d.status.lastReport)}
                </div>
            )}
        </div>
    );
}


function ResourcesTable({ rows, history, onSelect }: { rows: ResourceRow[]; history?: boolean; onSelect?: (name: string) => void }) {
    return (
        <DataTable<ResourceRow>
            rows={rows}
            rowKey={(r) => r.name}
            defaultSort={history ? 'cpuMsPs' : 'score'}
            onRowClick={onSelect ? (r) => onSelect(r.name) : undefined}
            empty={history ? 'No history recorded yet.' : 'No hook report yet. Install the hook and restart the resources.'}
            columns={[
                ...(history ? [] : [
                    { key: 'status', label: 'Status', value: (r: ResourceRow) => (r.status === 'critical' ? 2 : r.status === 'warning' ? 1 : 0), render: (r: ResourceRow) => <StatusLabel status={r.status ?? 'ok'} /> },
                ]),
                { key: 'name', label: 'Resource', render: (r) => mono(r.name) },
                ...(history ? [] : [
                    { key: 'score', label: 'Score', align: 'right' as const, title: 'load score 0-100 (CPU, max handler, bandwidth, alerts)' },
                ]),
                { key: 'cpuMsPs', label: 'CPU ms/s', align: 'right', render: (r) => fmtNum(r.cpuMsPs, 3) },
                { key: 'cpuMax', label: 'Max handler', align: 'right', render: (r) => fmtMs(r.cpuMax) },
                { key: 'netInBps', label: 'Net in', align: 'right', render: (r) => <>{fmtBytes(r.netInBps)}/s <span className="text-muted-foreground">· {fmtNum(r.netInPs)}/s</span></> },
                { key: 'outClKiBps', label: 'Out to clients', align: 'right', render: (r) => <>{fmtNum(r.outClKiBps, 2)} KiB/s <span className="text-muted-foreground">· {fmtNum(r.outClPs)}/s</span></> },
                { key: 'locInPs', label: 'Local in', align: 'right', render: (r) => rate(r.locInPs) },
                { key: 'locOutPs', label: 'Local out', align: 'right', render: (r) => rate(r.locOutPs) },
                { key: 'httpPm', label: 'HTTP /min', align: 'right', render: (r) => <>{rate(r.httpPm)}{r.httpErr ? <span className="text-[#d03b3b]"> · {r.httpErr} err</span> : null}</> },
            ]}
        />
    );
}
